package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.state.SHOW_REVAMP
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminTopFavoritedEntity
import twist.mena.sports.shared.dto.admin.AdminUserAnalyticsResponse

@Composable
fun AnalyticsScreen(apiClient: AdminApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var state by remember { mutableStateOf<UiState<AdminUserAnalyticsResponse>>(UiState.Loading) }
    var refreshKey by remember { mutableStateOf(0) }

    LaunchedEffect(refreshKey) {
        scope.launch {
            try {
                val prev = (state as? UiState.Success)?.data
                if (prev == null) state = UiState.Loading
                state = UiState.Success(apiClient.getUserAnalytics())
            } catch (e: Exception) {
                state = UiState.Error("Failed to load analytics: ${e.message}")
            }
        }
    }

    // Refresh header
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            marginBottom(Spacing.lg)
        }
    }) {
        H1({
            style {
                marginTop(0.px); marginBottom(0.px)
                fontSize(Typography.xl)
                color(Color(colors.text))
                fontWeight("700")
            }
        }) { Text("Analytics") }
        Button({
            style {
                padding(Spacing.xs, Spacing.md)
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
                borderRadius(BorderRadius.md)
                property("border", "1px solid ${colors.border}")
                property("cursor", "pointer")
                fontSize(Typography.sm)
                fontWeight("500")
            }
            onClick { refreshKey++ }
        }) {
            Text(if (state is UiState.Loading) "Refreshing..." else "Refresh")
        }
    }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading analytics...") }
        is UiState.Error -> Div({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            val data = s.data

            // User Overview
            AnalyticsSectionTitle("User Overview", colors)
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.md); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "Total Users", data.totalUsers.toString(), colors.primary)
                StatCard(colors, "New Today", data.newUsersToday.toString(), colors.success)
                StatCard(colors, "New This Week", data.newUsersThisWeek.toString(), colors.info)
                StatCard(colors, "New This Month", data.newUsersThisMonth.toString(), colors.info)
            }
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.md); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "DAU (24h)", data.dau.toString(), colors.success)
                StatCard(colors, "Active 7d", data.active7d.toString(), colors.info)
                StatCard(colors, "MAU (30d)", data.mau.toString(), colors.info)
                StatCard(colors, "DAU/MAU", "${(data.dauMauRatio * 100).toInt()}%", colors.primary)
            }
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.xxl); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "Has Favorites", data.onboardingCompletedCount.toString(), colors.success)
                StatCard(colors, "Favorites Rate", "${data.onboardingCompletionRate}%", colors.info)
            }

            // User Growth sparkline
            if (data.dailySignups.isNotEmpty()) {
                AnalyticsSectionTitle("User Growth (last 30 days)", colors)
                SparklineChart(colors, data.dailySignups)
            }

            // Distribution
            AnalyticsSectionTitle("Distribution", colors)
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr 1fr 1fr")
                    gap(Spacing.lg)
                    marginBottom(Spacing.xxl)
                }
            }) {
                DistributionCard(colors, "Users by Country", data.usersByCountry)
                DistributionCard(colors, "Users by Login Type", data.usersByLoginType)
                DistributionCard(colors, "Users by Timezone", data.usersByTimezone)
            }

            // Devices
            AnalyticsSectionTitle("Devices", colors)
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.md); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "Total Devices", data.totalDevices.toString(), colors.primary)
                StatCard(colors, "Active", data.activeDevices.toString(), colors.success)
                StatCard(colors, "Revoked", data.revokedDevices.toString(), colors.danger)
            }
            if (data.devicesByPlatform.isNotEmpty()) {
                Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.xxl); property("flex-wrap", "wrap") } }) {
                    data.devicesByPlatform.forEach { (platform, count) ->
                        StatCard(colors, platform, count.toString(), colors.info)
                    }
                }
            }

            // Favorites
            AnalyticsSectionTitle(if (SHOW_REVAMP) "Favorites (Revamp + Legacy)" else "Favorites", colors)
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.md); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "Team Favs", data.totalTeamFavorites.toString(), colors.primary)
                StatCard(colors, "League Favs", data.totalLeagueFavorites.toString(), colors.info)
                StatCard(colors, "Player Favs", data.totalPlayerFavorites.toString(), colors.success)
                StatCard(colors, "Avg per User", data.avgFavoritesPerUser.toString(), colors.primary)
            }
            if (data.favoritesDistribution.isNotEmpty()) {
                Div({ style { marginBottom(Spacing.lg) } }) {
                    DistributionCard(colors, "Favorites Distribution (users by # of favorites)", data.favoritesDistribution)
                }
            }
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr 1fr 1fr")
                    gap(Spacing.lg)
                    marginBottom(Spacing.xxl)
                }
            }) {
                RankedListCard(colors, "Top Teams", data.topFavoritedTeams)
                RankedListCard(colors, "Top Leagues", data.topFavoritedLeagues)
                RankedListCard(colors, "Top Players", data.topFavoritedPlayers)
            }

            // Notifications
            AnalyticsSectionTitle("Notifications", colors)
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.xxl); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "Delivery Rate", "${data.notificationDeliveryRate}%", colors.success)
                StatCard(colors, "Read Rate", "${data.notificationReadRate}%", colors.info)
            }

            // Predictions
            AnalyticsSectionTitle("Predictions", colors)
            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginBottom(Spacing.xxl); property("flex-wrap", "wrap") } }) {
                StatCard(colors, "Total", data.totalPredictions.toString(), colors.primary)
                StatCard(colors, "Today", data.predictionsToday.toString(), colors.success)
                StatCard(colors, "This Week", data.predictionsThisWeek.toString(), colors.info)
            }
        }
    }
}

@Composable
private fun AnalyticsSectionTitle(title: String, colors: AppColors) {
    H2({
        style {
            marginTop(0.px)
            marginBottom(Spacing.md)
            fontSize(Typography.lg)
            color(Color(colors.textMuted))
            fontWeight("600")
        }
    }) { Text(title) }
}

@Composable
private fun StatCard(colors: AppColors, label: String, value: String, accentColor: String) {
    Div({
        style {
            padding(Spacing.sm, Spacing.md)
            backgroundColor(Color(colors.surface))
            borderRadius(Spacing.xs)
            property("border", "1px solid ${colors.border}")
            property("border-left", "3px solid $accentColor")
            property("min-width", "120px"); property("flex", "1")
        }
    }) {
        Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text(label) }
        Div({ style { fontSize(Typography.lg); fontWeight("700"); color(Color(colors.text)) } }) { Text(value) }
    }
}

@Composable
private fun SparklineChart(colors: AppColors, data: Map<String, Int>) {
    val sorted = data.entries.sortedBy { it.key }
    val maxVal = sorted.maxOfOrNull { it.value } ?: 1
    val chartHeight = 120

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.lg)
            property("border", "1px solid ${colors.border}")
            marginBottom(Spacing.xxl)
        }
    }) {
        // Bars
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.FlexEnd)
                gap(2.px)
                height(chartHeight.px)
            }
        }) {
            sorted.forEach { (date, count) ->
                val barHeight = if (maxVal > 0) (count.toDouble() / maxVal * chartHeight).toInt() else 0
                Div({
                    style {
                        flex(1)
                        height(barHeight.coerceAtLeast(2).px)
                        backgroundColor(Color(colors.primary))
                        borderRadius(2.px)
                        property("transition", "height 0.3s ease")
                    }
                    title("$date: $count signups")
                })
            }
        }
        // Date labels
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginTop(Spacing.xs)
            }
        }) {
            val first = sorted.firstOrNull()?.key?.takeLast(5) ?: ""
            val last = sorted.lastOrNull()?.key?.takeLast(5) ?: ""
            Span({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text(first) }
            Span({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text(last) }
        }
    }
}

@Composable
private fun DistributionCard(colors: AppColors, title: String, data: Map<String, Int>) {
    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.lg)
            property("border", "1px solid ${colors.border}")
        }
    }) {
        H3({
            style {
                marginTop(0.px)
                marginBottom(Spacing.md)
                fontSize(Typography.base)
                color(Color(colors.text))
                fontWeight("600")
            }
        }) { Text(title) }

        if (data.isEmpty()) {
            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text("No data") }
        } else {
            val sorted = data.entries.sortedByDescending { it.value }
            val maxVal = sorted.firstOrNull()?.value ?: 1
            sorted.take(15).forEach { (key, count) ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Span({
                        style {
                            property("min-width", "60px")
                            fontSize(Typography.sm)
                            color(Color(colors.text))
                            fontWeight("500")
                        }
                    }) { Text(key.ifBlank { "unknown" }) }
                    Div({
                        style {
                            flex(1)
                            height(16.px)
                            backgroundColor(Color(colors.background))
                            borderRadius(Spacing.xs)
                            property("overflow", "hidden")
                        }
                    }) {
                        Div({
                            style {
                                height(100.percent)
                                width((count.toDouble() / maxVal * 100).percent)
                                backgroundColor(Color(colors.primary))
                                borderRadius(Spacing.xs)
                            }
                        })
                    }
                    Span({
                        style {
                            property("min-width", "40px")
                            textAlign("right")
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                        }
                    }) { Text(count.toString()) }
                }
            }
        }
    }
}

@Composable
private fun RankedListCard(colors: AppColors, title: String, items: List<AdminTopFavoritedEntity>) {
    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.lg)
            property("border", "1px solid ${colors.border}")
        }
    }) {
        H3({
            style {
                marginTop(0.px)
                marginBottom(Spacing.md)
                fontSize(Typography.base)
                color(Color(colors.text))
                fontWeight("600")
            }
        }) { Text(title) }

        if (items.isEmpty()) {
            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text("No data") }
        } else {
            val maxCount = items.firstOrNull()?.count ?: 1
            items.forEachIndexed { index, item ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                        padding(Spacing.xs, 0.px)
                        if (index > 0) property("border-top", "1px solid ${colors.border}")
                    }
                }) {
                    // Rank number
                    Span({
                        style {
                            property("min-width", "24px")
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            fontWeight("600")
                        }
                    }) { Text("${index + 1}.") }

                    // Logo
                    if (!item.logoUrl.isNullOrBlank()) {
                        Img(src = item.logoUrl!!) {
                            style {
                                width(24.px)
                                height(24.px)
                                borderRadius(50.percent)
                                property("object-fit", "cover")
                                property("flex-shrink", "0")
                            }
                        }
                    }

                    // Name + source badge
                    Div({ style { flex(1); property("min-width", "0") } }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(4.px)
                            }
                        }) {
                            Span({ style { fontSize(Typography.sm); color(Color(colors.text)) } }) {
                                Text(item.nameAr ?: item.nameEn)
                            }
                            if (SHOW_REVAMP && item.source != null) {
                                Span({
                                    style {
                                        fontSize(10.px)
                                        padding(1.px, 4.px)
                                        borderRadius(3.px)
                                        backgroundColor(Color(if (item.source == "legacy") "#f59e0b" else colors.primary))
                                        color(Color("#fff"))
                                        fontWeight("600")
                                        property("line-height", "1.2")
                                    }
                                }) { Text(item.source!!) }
                            }
                        }
                        if (!item.nameAr.isNullOrBlank()) {
                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                Text(item.nameEn)
                            }
                        }
                    }

                    // Count with percentage bar
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.xs)
                            property("min-width", "80px")
                        }
                    }) {
                        Div({
                            style {
                                width(40.px)
                                height(6.px)
                                backgroundColor(Color(colors.background))
                                borderRadius(3.px)
                                property("overflow", "hidden")
                            }
                        }) {
                            Div({
                                style {
                                    height(100.percent)
                                    width((item.count.toDouble() / maxCount * 100).percent)
                                    backgroundColor(Color(colors.primary))
                                    borderRadius(3.px)
                                }
                            })
                        }
                        Span({
                            style {
                                fontSize(Typography.sm)
                                color(Color(colors.primary))
                                fontWeight("700")
                                property("min-width", "30px")
                                textAlign("right")
                            }
                        }) { Text(item.count.toString()) }
                    }
                }
            }
        }
    }
}
